import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getTickets, getTicketFilterOptions } from '@/services/api';
import { TICKET_STATUS_LABELS, TICKET_PRIORITY_LABELS, TICKET_CATEGORY_LABELS } from '@/types';
import type { TicketStatus, TicketPriority, TicketCategory } from '@/types';
import { useScopeStore } from '@/features/scope/useScopeStore';
import { formatRelativeTime } from '@/utils/dateFormatting';
import { useTicketsGridState } from '@/features/tickets/useTicketsGridState';
import GridColumnHeader from '@/components/GridColumnHeader';

const PRIORITY_COLORS: Record<TicketPriority, string> = {
  'Low': 'bg-brand-surface text-brand-text-secondary',
  'Normal': 'bg-brand-status-in-progress-bg text-brand-status-in-progress',
  'High': 'bg-brand-priority-high/20 text-brand-text-primary',
  'Urgent': 'bg-brand-priority-urgent/20 text-brand-priority-urgent',
};

const TICKET_STATUS_COLORS: Record<string, string> = {
  Open:            'bg-brand-status-open-bg text-brand-status-open',
  InProgress:      'bg-brand-status-in-progress-bg text-brand-status-in-progress',
  WaitingOnClient: 'bg-brand-status-waiting-bg text-brand-status-waiting',
  Resolved:        'bg-brand-status-resolved-bg text-brand-status-resolved',
  Closed:          'bg-brand-status-closed-bg text-brand-status-closed',
};
const TICKET_CATEGORY_COLORS: Record<string, string> = {
  Support: 'bg-brand-status-new-bg text-brand-status-new',
  Sales:   'bg-brand-status-qualified-bg text-brand-status-qualified',
};

export default function TicketList() {
  const { scope } = useScopeStore();
  const ownerFilter = scope === 'mine' ? 'me' : undefined;
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const pageSize = 20;
  const { sortBy, sortDir, page, filters, setSort, setPage, setFilter, clearAllFilters } = useTicketsGridState();

  const TICKET_STATUS_OPTIONS = ['Open', 'InProgress', 'WaitingOnClient', 'Resolved', 'Closed'];
  const TICKET_PRIORITY_OPTIONS = ['Low', 'Normal', 'High', 'Urgent'];
  const TICKET_CATEGORY_OPTIONS = ['Support', 'Sales'];
  const CUSTOMER_RESPONDED_OPTIONS = ['Customer', 'Cre8or'];

  const { data: ownerOptions, isLoading: ownerLoading } = useQuery({
    queryKey: ['ticket-filter-options', 'owner'],
    queryFn: () => getTicketFilterOptions('owner'),
    staleTime: 60_000,
  });
  const { data: companyOptions, isLoading: companyLoading } = useQuery({
    queryKey: ['ticket-filter-options', 'companyName'],
    queryFn: () => getTicketFilterOptions('companyName'),
    staleTime: 60_000,
  });

  const tabs = [
    { label: 'All', value: undefined },
    { label: 'Open', value: 'Open' },
    { label: 'In Progress', value: 'InProgress' },
    { label: 'Waiting on Client', value: 'WaitingOnClient' },
    { label: 'Resolved', value: 'Resolved' },
    { label: 'Closed', value: 'Closed' },
  ];

  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', { status: statusFilter, scope, page, sortBy, sortDir, filters }],
    queryFn: () => getTickets({
      status: statusFilter,
      ownerFilter,
      page,
      pageSize,
      sortBy,
      sortDir,
      filter: Object.keys(filters).length > 0 ? filters : undefined,
    }),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <div>
          <h1 className="text-3xl font-bold text-brand-text-primary">Tickets</h1>
          <p className="text-sm italic text-brand-subtitle mt-0.5">Manage and track your support tickets</p>
        </div>
        <Link
            to="/tickets/create"
            className="px-4 py-2 bg-brand-accent text-white rounded-full hover:bg-brand-accent-hover focus:outline-none focus:ring-2 focus:ring-brand-accent transition-colors font-medium text-sm"        >
          Create Ticket
        </Link>
      </div>

      {/* Status filter tabs — pill chips */}
      <div className="flex gap-2 mb-6 flex-wrap mt-4">
        {tabs.map((tab) => (
          <button
            key={tab.label}
            onClick={() => {
              setStatusFilter(tab.value);
              setPage(1);
            }}
            className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors whitespace-nowrap ${
              statusFilter === tab.value
                ? 'bg-brand-filter-tab-active-bg text-brand-filter-tab-active-text'
                : 'bg-brand-filter-tab-bg text-brand-filter-tab-text border border-brand-border hover:bg-brand-border'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-filter-tab-active-bg"></div>
          <span className="ml-3 text-brand-text-secondary">Loading tickets...</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="text-center p-8">
          <p className="text-brand-priority-urgent mb-2">Unable to load tickets</p>
          <p className="text-sm text-brand-text-secondary">Please check your permissions or try again later</p>
        </div>
      )}

      {!isLoading && !error && (!data || data.items.length === 0) && (
        <div className="text-center p-8 text-brand-text-secondary">
          <p>No tickets found</p>
          {Object.keys(filters).length > 0 && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="mt-3 text-sm text-brand-accent hover:text-brand-accent-hover underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {!isLoading && data && data.items.length > 0 && (
        <>
          <div className="bg-white rounded shadow overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-max w-full text-sm">
              <thead className="bg-brand-accent/20 border-b border-brand-accent/30">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-brand-text-secondary whitespace-nowrap">Ticket ID</th>
                  <GridColumnHeader label="Subject"      column="subject"      activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort} />
                  <GridColumnHeader label="Company Name" column="companyName"  activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort}
                    filterOptions={companyOptions ?? []} isFilterLoading={companyLoading} activeFilterValues={(filters['companyName'] ?? '').split(',').filter(Boolean)} onFilterChange={setFilter} />
                  <GridColumnHeader label="Category"     column="category"     activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort}
                    filterOptions={TICKET_CATEGORY_OPTIONS} activeFilterValues={(filters['category'] ?? '').split(',').filter(Boolean)} onFilterChange={setFilter} />
                  <GridColumnHeader label="Status"       column="status"       activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort}
                    filterOptions={TICKET_STATUS_OPTIONS} activeFilterValues={(filters['status'] ?? '').split(',').filter(Boolean)} onFilterChange={setFilter} />
                  <GridColumnHeader label="Priority"     column="priority"     activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort}
                    filterOptions={TICKET_PRIORITY_OPTIONS} activeFilterValues={(filters['priority'] ?? '').split(',').filter(Boolean)} onFilterChange={setFilter} />
                  <GridColumnHeader label="Owner"        column="owner"        activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort}
                    filterOptions={ownerOptions ?? []} isFilterLoading={ownerLoading} activeFilterValues={(filters['owner'] ?? '').split(',').filter(Boolean)} onFilterChange={setFilter} />
                  <GridColumnHeader label="Response" column="customerResponded" activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort}
                    filterOptions={CUSTOMER_RESPONDED_OPTIONS} activeFilterValues={(filters['customerResponded'] ?? '').split(',').filter(Boolean)} onFilterChange={setFilter} />
                  <GridColumnHeader label="Created"      column="createdAt"    activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort} />
                  <GridColumnHeader label="Updated"      column="updatedAt"    activeSortCol={sortBy} activeSortDir={sortDir} onSortChange={setSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {data.items.map(t => (
                  <tr
                    key={t.ticketId}
                    data-unread={String(!!t.isUnread)}
                    className={`cursor-pointer transition-colors ${
                      t.isUnread
                        ? 'bg-brand-accent/10 border-l-4 border-brand-accent hover:bg-brand-accent/15'
                        : 'hover:bg-brand-surface'
                    }`}
                    onClick={() => window.location.href = `/tickets/${t.ticketId}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-brand-text-secondary">{t.internalId || '—'}</td>
                    <td className="px-3 py-2 font-medium">
                      <div className="truncate" title={t.subject}>
                        {t.subject}
                      </div>
                    </td>
                    <td className="px-3 py-2 font-medium truncate">
                      {t.clientInfo?.accountId
                        ? <Link to={`/clients/${t.clientInfo.accountId}`} onClick={e => e.stopPropagation()} className="hover:underline text-brand-accent">{t.clientInfo.companyName}</Link>
                        : (t.clientInfo?.companyName || '—')
                      }
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${TICKET_CATEGORY_COLORS[t.category] ?? 'bg-gray-100 text-gray-600'}`}>
                        {TICKET_CATEGORY_LABELS[t.category as TicketCategory]}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs ${TICKET_STATUS_COLORS[t.status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {TICKET_STATUS_LABELS[t.status as TicketStatus]}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${PRIORITY_COLORS[t.priority as TicketPriority]}`}>
                        {TICKET_PRIORITY_LABELS[t.priority as TicketPriority]}
                      </span>
                    </td>
                    {/* T036: FR-027 — show ownerDisplayName, fall back to 'Unassigned' */}
                    <td className="px-3 py-2 text-brand-text-secondary truncate">{t.ownerDisplayName ?? 'Unassigned'}</td>
                    <td className="px-3 py-2">
                      {t.customerResponded
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-accent/15 text-brand-accent">&#10003; Customer</span>
                        : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Cre8or</span>
                      }
                    </td>
                    <td className="px-3 py-2 text-brand-text-secondary">{new Date(t.createdAt).toLocaleDateString()}</td>
                    {/* US3 (Feature 039): Updated column — relative time with tooltip */}
                    <td className="px-3 py-2 text-brand-text-secondary">
                      {t.updatedAt ? (() => {
                        const [rel, abs] = formatRelativeTime(t.updatedAt);
                        return <span title={abs}>{rel}</span>;
                      })() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4 text-sm text-brand-text-secondary">
            <span>{data.total} total</span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >Prev</button>
              <span className="px-2 py-1">Page {page}</span>
              <button
                disabled={page * pageSize >= data.total}
                onClick={() => setPage(page + 1)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
